import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import octopusInstance from "../services/octopus/octopusInstance";
import { updateBrokerageFromSocket } from "../store/actions/brokerage";

function useBrokerageUpdates() {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const handlerRef = useRef(null);
  const setupAttempts = useRef(0);

  const setupHandler = () => {
    try {
      const clientId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");
      
      if (!clientId || !authToken) {
        return;
      }

      // Force close any existing connection
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }

      handlerRef.current = octopusInstance.wsHandler({
        messageType: "BrokerageMessage",
        subscriptionLocation: "brokerage-socket",
        payload: {
          clientId: clientId,
        },
      });

      if (handlerRef.current) {
        handlerRef.current.subscribe(({ msg }) => {
          // Check if this is a brokerage message and dispatch to Redux store
          if (msg && typeof msg === 'object') {
            console.log("brokerage decoded", msg);
            
            // Check if this message contains brokerage data
            if (msg.brokerage_rules && Array.isArray(msg.brokerage_rules)) {
              console.log("Received brokerage update from socket:", msg);
              console.log("Dispatching brokerage data to Redux store");
              dispatch(updateBrokerageFromSocket(msg));
            }
          }
        });
      }
    } catch (error) {
      console.error('Error setting up brokerage socket handler:', error);
    }
  };

  // Attempt connection when auth state changes
  useEffect(() => {
    if (auth?.isLoggedIn) {
      setupHandler();
    }
  }, [auth?.isLoggedIn, dispatch]);

  // Retry connection on mount and periodically if needed
  useEffect(() => {
    const attemptConnection = () => {
      const authToken = localStorage.getItem("auth_token");
      const clientId = localStorage.getItem("login_id");
      
      if (authToken && clientId && !handlerRef.current && setupAttempts.current < 3) {
        setupAttempts.current += 1;
        setupHandler();
      }
    };

    // Initial attempt
    attemptConnection();

    // Retry after a short delay
    const retryTimer = setInterval(() => {
      if (!handlerRef.current && setupAttempts.current < 3) {
        attemptConnection();
      }
    }, 2000);

    return () => {
      clearInterval(retryTimer);
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }
    };
  }, [dispatch]);

  // Reconnect on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setupAttempts.current = 0; // Reset attempts counter
        setupHandler();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  return null;
}

export default useBrokerageUpdates;